import { Application, Container, Graphics, Text, Sprite, Texture } from 'pixi.js';
import { Cell } from '../../ultis/types';
import { fullcolor, listIndexIn128 } from '../../ultis/helper';
import { UpdateCellRequest } from '../../services/socket.service';

interface PixelEditorProps {
  app: Application;
  cellData: Cell;
  neighborCells: (Cell | null)[];
  onClose: () => void;
  onSave: (updatedData: UpdateCellRequest) => void;
  onSaveDraft: (draftName: string, colors: string[]) => void;
}

export class PixelEditor {
  private app: Application;
  private cellData: Cell;
  private neighborCells: (Cell | null)[];
  private onClose: () => void;
  private onSave: (updatedData: UpdateCellRequest) => void;
  private onSaveDraft: (draftName: string, colors: string[]) => void;

  private popupContainer: Container | null = null;
  private selectedColorRef: string = 'ffffff';
  private gridCellsRef: Graphics[] = [];
  private gridColorsRef: string[] = [];
  private neighborCellsRef: Graphics[] = [];
  private colorPickerOverlay: Container | null = null;
  private eyedropperMode = { active: false };

  constructor(props: PixelEditorProps) {
    this.app = props.app;
    this.cellData = props.cellData;
    this.neighborCells = props.neighborCells;
    this.onClose = props.onClose;
    this.onSave = props.onSave;
    this.onSaveDraft = props.onSaveDraft;
  }

  public show() {
    if (!this.app?.stage) return;

    // Tạo popup container
    this.popupContainer = new Container();
    this.popupContainer.zIndex = 2000;

    // Tạo overlay để chặn tương tác với map
    const overlay = new Graphics();
    overlay.rect(0, 0, this.app.screen.width, this.app.screen.height);
    overlay.fill({ color: 0x000000, alpha: 0.7 });
    overlay.eventMode = 'static';
    overlay.cursor = 'default';

    // Chặn tất cả các loại events
    overlay.on('pointerdown', (e: any) => e.stopPropagation());
    overlay.on('pointermove', (e: any) => e.stopPropagation());
    overlay.on('pointerup', (e: any) => e.stopPropagation());
    overlay.on('wheel', (e: any) => e.stopPropagation());
    overlay.on('touchstart', (e: any) => e.stopPropagation());
    overlay.on('touchmove', (e: any) => e.stopPropagation());
    overlay.on('touchend', (e: any) => e.stopPropagation());

    this.popupContainer.addChild(overlay);

    // Tính toán kích thước
    const gridSize = 10;
    const cellSize = 25; // Mỗi cell là 25px
    const gridTotalSize = gridSize * cellSize; // 250px
    const neighborSize = 2 * cellSize; // 2 cells = 50px mỗi bên
    const gap = 2; // Gap giữa center và neighbors
    const gridPopupSize = neighborSize + gap + gridTotalSize + gap + neighborSize; // 50 + 2 + 250 + 2 + 50 = 354px

    // Tính toán vị trí center grid
    // neighbor 50 + gap 2 + center 250 + gap 2 + neighbor 50 = 354
    const gridX = neighborSize + gap; // Vị trí X của center grid trong popup
    const gridY = neighborSize + gap; // Vị trí Y của center grid trong popup

    // ============================================================================
    // POPUP GRID (Center + Neighbor) - Popup vuông ở giữa
    // ============================================================================
    const gridPopupX = (this.app.screen.width - gridPopupSize) / 2;
    const gridPopupY = (this.app.screen.height - gridPopupSize) / 2;

    // Tạo popup background cho grid
    const gridPopupBg = new Graphics();
    gridPopupBg.rect(0, 0, gridPopupSize, gridPopupSize);
    gridPopupBg.fill({ color: 0x000000 }); // Background màđen
    gridPopupBg.stroke({ color: 0xffffff, width: 3 }); // Border màu trắng để nổi bật

    const gridPopupContainer = new Container();
    gridPopupContainer.position.set(gridPopupX, gridPopupY);
    gridPopupContainer.addChild(gridPopupBg);

    // Tạo mask cho grid popup - mask bằng với popup size (354x354)
    const mask = new Graphics();
    mask.rect(0, 0, gridPopupSize, gridPopupSize);
    mask.fill({ color: 0xffffff });
    gridPopupContainer.addChild(mask);
    gridPopupContainer.mask = mask;

    // Create neighbor preview (around the grid) - trong gridPopupContainer
    this.createNeighborPreview(gridPopupContainer, gridX, gridY, gridSize, cellSize, gridTotalSize, neighborSize, gap);

    // Create grid trong popup vuông
    this.createGrid(gridPopupContainer, gridX, gridY, gridSize, cellSize, gridTotalSize);

    this.popupContainer.addChild(gridPopupContainer);

    // ============================================================================
    // TOOLS BAR - Ngay bên dưới grid popup, tất cả buttons xếp hàng ngang
    // ============================================================================
    const toolsBarHeight = 50; // Chiều cao tools bar
    const toolsBarGap = 10; // Khoảng cách giữa grid popup và tools bar
    const toolsBarY = gridPopupY + gridPopupSize + toolsBarGap; // Ngay bên dưới grid popup
    const toolsBarWidth = gridPopupSize; // Cùng width với grid popup

    // Tạo background cho tools bar
    const toolsBarBg = new Graphics();
    toolsBarBg.rect(0, 0, toolsBarWidth, toolsBarHeight);
    toolsBarBg.fill({ color: 0xffffff, alpha: 0.95 });
    toolsBarBg.stroke({ color: 0x000000, width: 2 });

    const toolsBarContainer = new Container();
    toolsBarContainer.position.set(gridPopupX, toolsBarY); // Cùng X với grid popup
    toolsBarContainer.addChild(toolsBarBg);

    // Tất cả buttons xếp hàng ngang: Color picker, Dropper, Clear, Cancel, Submit
    const buttonSize = 40;
    const buttonGap = 10;
    const totalButtons = 5; // Color picker, Dropper, Clear, Cancel, Submit
    const totalWidth = buttonSize * totalButtons + buttonGap * (totalButtons - 1);
    const buttonsStartX = (toolsBarWidth - totalWidth) / 2; // Căn giữa trong tools bar
    const buttonY = (toolsBarHeight - buttonSize) / 2; // Căn giữa theo chiều dọc

    // Color picker button
    this.createColorPickerButton(toolsBarContainer, buttonsStartX, buttonY, buttonSize);

    // Eyedropper button
    this.createEyedropperButton(toolsBarContainer, buttonsStartX + buttonSize + buttonGap, buttonY, buttonSize);

    // Clear, Cancel, Submit buttons
    this.createActionButtons(
      toolsBarContainer,
      buttonsStartX + (buttonSize + buttonGap) * 2,
      buttonY,
      buttonSize,
      buttonGap,
      gridSize,
      cellSize
    );

    this.popupContainer.addChild(toolsBarContainer);
    this.app.stage.addChild(this.popupContainer);
  }

  private createGrid(
    contentContainer: Container,
    gridX: number,
    gridY: number,
    gridSize: number,
    cellSize: number,
    gridTotalSize: number
  ) {
    const gridContainer = new Container();
    gridContainer.position.set(gridX, gridY);
    this.gridCellsRef = [];
    this.gridColorsRef = [];

    const existingColors = this.cellData.c || Array(100).fill(0xffffff);
    const isPaintingRef = { current: false };

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const cellIndex = row * gridSize + col;
        const cell = new Graphics();
        const x = col * cellSize;
        const y = row * cellSize;

        let colorValue = existingColors[cellIndex] || 0xffffff;
        let colorHex = typeof colorValue === 'number'
          ? colorValue.toString(16).padStart(6, '0')
          : colorValue.toString().padStart(6, '0');

        this.gridColorsRef[cellIndex] = colorHex;

        cell.rect(x, y, cellSize, cellSize);
        cell.fill({ color: parseInt(colorHex, 16) });
        cell.stroke({ color: 0x000000, width: 1 });

        cell.eventMode = 'static';
        cell.cursor = 'pointer';
        (cell as any).cellIndex = cellIndex;

        const paintCell = (idx: number, cx: number, cy: number, graphics: Graphics) => {
          const selectedColor = this.selectedColorRef;
          this.gridColorsRef[idx] = selectedColor;
          graphics.clear();
          graphics.rect(cx, cy, cellSize, cellSize);
          graphics.fill({ color: parseInt(selectedColor, 16) });
          graphics.stroke({ color: 0x000000, width: 1 });
        };

        cell.on('pointerdown', (e: any) => {
          e.stopPropagation();
          if (this.eyedropperMode.active) {
            const pickedColor = this.gridColorsRef[cellIndex];
            this.selectedColorRef = pickedColor;
            console.log('Picked color:', pickedColor);
            this.updateColorButton();
            this.eyedropperMode.active = false;
            this.updateEyedropperButton();
          } else {
            isPaintingRef.current = true;
            paintCell(cellIndex, x, y, cell);
          }
        });

        cell.on('pointerup', () => {
          isPaintingRef.current = false;
        });

        cell.on('pointerupoutside', () => {
          isPaintingRef.current = false;
        });

        cell.on('pointerover', () => {
          if (isPaintingRef.current) {
            paintCell(cellIndex, x, y, cell);
          }
        });

        gridContainer.addChild(cell);
        this.gridCellsRef.push(cell);
      }
    }

    if (this.popupContainer) {
      this.popupContainer.on('pointerup', () => {
        isPaintingRef.current = false;
      });
      this.popupContainer.on('pointerupoutside', () => {
        isPaintingRef.current = false;
      });
    }

    contentContainer.addChild(gridContainer);
  }

  private createNeighborPreview(
    contentContainer: Container,
    gridX: number,
    gridY: number,
    gridSize: number,
    cellSize: number,
    gridTotalSize: number,
    neighborSize: number,
    gap: number
  ) {
    const neighborContainer = new Container();

    // Neighbor positions: [top-left, top, top-right, left, right, bottom-left, bottom, bottom-right]
    // 4 hướng chính (top, bottom, left, right): 20 cells mỗi neighbor
    // 4 góc (top-left, top-right, bottom-left, bottom-right): 4 cells mỗi neighbor (2x2)
    // Có gap giữa neighbors và center grid
    const neighborPositions = [
      {
        x: gridX - neighborSize - gap,
        y: gridY - neighborSize - gap,
        rows: 2,
        cols: 2,
        startRow: 8, // Góc dưới phải của neighbor
        startCol: 8
      }, // top-left: 4 cells (2x2) - vẽ góc dưới phải
      {
        x: gridX,
        y: gridY - neighborSize - gap,
        rows: 2,
        cols: gridSize, // 20 cells (2 rows x 10 cols)
        startRow: 8, // 2 hàng cuối cùng
        startCol: 0
      }, // top: vẽ 2 hàng cuối cùng
      {
        x: gridX + gridTotalSize + gap,
        y: gridY - neighborSize - gap,
        rows: 2,
        cols: 2,
        startRow: 8, // Góc dưới trái của neighbor
        startCol: 0
      }, // top-right: 4 cells (2x2) - vẽ góc dưới trái
      {
        x: gridX - neighborSize - gap,
        y: gridY,
        rows: gridSize, // 20 cells (10 rows x 2 cols)
        cols: 2,
        startRow: 0,
        startCol: 8 // 2 cột cuối cùng (gần center nhất)
      }, // left: vẽ 2 cột cuối cùng
      {
        x: gridX + gridTotalSize + gap,
        y: gridY,
        rows: gridSize, // 20 cells (10 rows x 2 cols)
        cols: 2,
        startRow: 0,
        startCol: 0 // 2 cột đầu tiên (gần center nhất)
      }, // right: vẽ 2 cột đầu tiên
      {
        x: gridX - neighborSize - gap,
        y: gridY + gridTotalSize + gap,
        rows: 2,
        cols: 2,
        startRow: 0, // Góc trên phải của neighbor
        startCol: 8
      }, // bottom-left: 4 cells (2x2) - vẽ góc trên phải
      {
        x: gridX,
        y: gridY + gridTotalSize + gap,
        rows: 2,
        cols: gridSize, // 20 cells (2 rows x 10 cols)
        startRow: 0, // 2 hàng đầu tiên
        startCol: 0
      }, // bottom: vẽ 2 hàng đầu tiên
      {
        x: gridX + gridTotalSize + gap,
        y: gridY + gridTotalSize + gap,
        rows: 2,
        cols: 2,
        startRow: 0, // Góc trên trái của neighbor
        startCol: 0
      }, // bottom-right: 4 cells (2x2) - vẽ góc trên trái
    ];

    this.neighborCellsRef = [];

    this.neighborCells.forEach((neighborCell, index) => {
      if (!neighborCell) return; // Skip if neighbor doesn't exist

      const pos = neighborPositions[index];
      const neighborGraphics = this.renderNeighborCell(
        neighborCell,
        pos.x,
        pos.y,
        pos.rows,
        pos.cols,
        cellSize,
        pos.startRow,
        pos.startCol,
      );

      neighborContainer.addChild(neighborGraphics);
      this.neighborCellsRef.push(neighborGraphics);
    });

    contentContainer.addChild(neighborContainer);
  }

  private renderNeighborCell(
    cell: Cell,
    x: number,
    y: number,
    rows: number, // Số rows cần render
    cols: number, // Số cols cần render
    cellSize: number,
    startRow: number, // Vị trí bắt đầu row trong neighbor grid (0-9)
    startCol: number, // Vị trí bắt đầu col trong neighbor grid (0-9)
  ): Graphics {
    const container = new Graphics();
    container.position.set(x, y);

    const existingColors = cell.c || Array(100).fill(0xffffff);
    const fullGridSize = 10; // Full grid size của neighbor cell (10x10)

    // Alpha cố định cho tất cả neighbor cells (dark hơn nhiều để center nổi bật)
    const neighborAlpha = 0.7;

    // Kiểm tra xem cell có được vẽ chưa (o === null nghĩa là chưa được vẽ)
    const isCellEmpty = cell.o === null;

    // Render chỉ số cells được chỉ định (rows x cols)
    // Render từ vị trí startRow, startCol trong neighbor grid (các cells gần center nhất)
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const neighborRow = startRow + row;
        const neighborCol = startCol + col;
        const cellIndex = neighborRow * fullGridSize + neighborCol;
        const cellX = col * cellSize;
        const cellY = row * cellSize;

        let colorValue = existingColors[cellIndex] || 0xffffff;
        let colorHex = typeof colorValue === 'number'
          ? colorValue.toString(16).padStart(6, '0')
          : colorValue.toString().padStart(6, '0');

        // Nếu cell trống (chưa được vẽ), hiển thị trắng với alpha
        // Nếu cell đã được vẽ, hiển thị với alpha để tối hơn
        const finalColor = isCellEmpty ? 0xffffff : parseInt(colorHex, 16);
        const finalAlpha = neighborAlpha; // Áp dụng neighborAlpha cho cả cell trống và đã vẽ

        const cellGraphics = new Graphics();
        cellGraphics.rect(cellX, cellY, cellSize, cellSize);
        cellGraphics.fill({ color: finalColor, alpha: finalAlpha });
        cellGraphics.stroke({ color: 0x000000, width: 1, alpha: finalAlpha * 0.5 });

        // Chỉ cho phép color picker (readonly)
        cellGraphics.eventMode = 'static';
        cellGraphics.cursor = this.eyedropperMode.active ? 'crosshair' : 'default';
        (cellGraphics as any).cellIndex = cellIndex;
        (cellGraphics as any).isNeighbor = true;
        (cellGraphics as any).neighborColor = colorHex;

        cellGraphics.on('pointerdown', (e: any) => {
          e.stopPropagation();
          if (this.eyedropperMode.active) {
            const pickedColor = (e.target as any).neighborColor;
            this.selectedColorRef = pickedColor;
            console.log('Picked color from neighbor:', pickedColor);
            this.updateColorButton();
            this.eyedropperMode.active = false;
            this.updateEyedropperButton();
          }
        });

        container.addChild(cellGraphics);
      }
    }

    return container;
  }

  private colorButton: Container | null = null;
  private eyedropperButton: Container | null = null;

  private updateColorButton() {
    if (!this.colorButton) return;

    this.colorButton.removeChildren();
    const colorButtonWidth = 40;
    const colorButtonHeight = 40;

    const buttonBg = new Graphics();
    buttonBg.rect(0, 0, colorButtonWidth, colorButtonHeight);
    buttonBg.fill({ color: parseInt(this.selectedColorRef, 16) });
    buttonBg.stroke({ color: 0x000000, width: 2 });
    this.colorButton.addChild(buttonBg);

    // Load palette icon
    const paletteIcon = Sprite.from(Texture.from('palette'));
    const iconSize = 24;
    paletteIcon.width = iconSize;
    paletteIcon.height = iconSize;
    paletteIcon.position.set(
      (colorButtonWidth - iconSize) / 2,
      (colorButtonHeight - iconSize) / 2
    );
    this.colorButton.addChild(paletteIcon);
  }

  private updateEyedropperButton() {
    if (!this.eyedropperButton) return;

    this.eyedropperButton.removeChildren();
    const eyedropperButtonWidth = 40;
    const colorButtonHeight = 40;

    const buttonBg = new Graphics();
    buttonBg.rect(0, 0, eyedropperButtonWidth, colorButtonHeight);

    if (this.eyedropperMode.active) {
      buttonBg.fill({ color: 0xcccccc }); // Nền xám khi active
      buttonBg.stroke({ color: 0x000000, width: 2 });
    } else {
      buttonBg.fill({ color: 0xffffff }); // Nền trắng khi không active
      buttonBg.stroke({ color: 0x000000, width: 2 });
    }

    this.eyedropperButton.addChild(buttonBg);

    // Load dropper icon
    const dropperIcon = Sprite.from(Texture.from('dropper'));
    const iconSize = 24;
    dropperIcon.width = iconSize;
    dropperIcon.height = iconSize;
    dropperIcon.position.set(
      (eyedropperButtonWidth - iconSize) / 2,
      (colorButtonHeight - iconSize) / 2
    );
    this.eyedropperButton.addChild(dropperIcon);

    // Update cursor for neighbor cells
    this.neighborCellsRef.forEach((neighborContainer) => {
      if (neighborContainer && neighborContainer.children) {
        neighborContainer.children.forEach((child: any) => {
          if (child && (child as any).isNeighbor) {
            child.cursor = this.eyedropperMode.active ? 'crosshair' : 'default';
          }
        });
      }
    });
  }

  private toggleColorPicker(colorButtonWidth: number, colorButtonHeight: number) {
    if (!this.popupContainer) return;

    if (this.colorPickerOverlay) {
      this.popupContainer.removeChild(this.colorPickerOverlay);
      this.colorPickerOverlay = null;
      return;
    }

    this.colorPickerOverlay = new Container();
    this.colorPickerOverlay.zIndex = 3000;

    const pickerBg = new Graphics();
    pickerBg.rect(0, 0, this.app.screen.width, this.app.screen.height);
    pickerBg.fill({ color: 0x000000, alpha: 0.5 });
    pickerBg.eventMode = 'static';
    pickerBg.on('pointerdown', (e: any) => {
      e.stopPropagation();
      if (this.colorPickerOverlay && this.popupContainer) {
        this.popupContainer.removeChild(this.colorPickerOverlay);
        this.colorPickerOverlay = null;
      }
    });
    this.colorPickerOverlay.addChild(pickerBg);

    const paletteWidth = 400;
    const paletteHeight = 300;
    const paletteX = (this.app.screen.width - paletteWidth) / 2;
    const paletteY = (this.app.screen.height - paletteHeight) / 2;

    const palettePanel = new Container();
    palettePanel.position.set(paletteX, paletteY);

    const panelBg = new Graphics();
    panelBg.rect(0, 0, paletteWidth, paletteHeight);
    panelBg.fill({ color: 0xffffff });
    panelBg.stroke({ color: 0x000000, width: 3 });
    palettePanel.addChild(panelBg);

    const paletteTitle = new Text('Chọn màu', {
      fontSize: 18,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
    });
    paletteTitle.position.set((paletteWidth - paletteTitle.width) / 2, 10);
    palettePanel.addChild(paletteTitle);

    const colorsPerRow = 16;
    const colorCellSize = (paletteWidth - 40) / colorsPerRow;
    const gridY = 50;

    fullcolor.forEach((colorHex, index) => {
      const row = Math.floor(index / colorsPerRow);
      const col = index % colorsPerRow;

      const colorCell = new Graphics();
      const x = 20 + col * colorCellSize;
      const y = gridY + row * colorCellSize;
      const colorValue = parseInt(colorHex, 16);

      colorCell.rect(x, y, colorCellSize, colorCellSize);
      colorCell.fill({ color: colorValue });

      if (colorHex === this.selectedColorRef) {
        colorCell.stroke({ color: 0xffffff, width: 3 });
      } else {
        colorCell.stroke({ color: 0x666666, width: 0.5 });
      }

      colorCell.eventMode = 'static';
      colorCell.cursor = 'pointer';

      colorCell.on('pointerdown', (e: any) => {
        e.stopPropagation();
        this.selectedColorRef = colorHex;
        console.log('Selected color:', colorHex);
        this.updateColorButton();

        if (this.colorPickerOverlay && this.popupContainer) {
          this.popupContainer.removeChild(this.colorPickerOverlay);
          this.colorPickerOverlay = null;
        }
      });

      palettePanel.addChild(colorCell);
    });

    this.colorPickerOverlay.addChild(palettePanel);
    this.popupContainer.addChild(this.colorPickerOverlay);
  }

  private createColorPickerButton(
    contentContainer: Container,
    x: number,
    y: number,
    buttonSize: number
  ) {
    this.colorButton = new Container();
    this.colorButton.position.set(x, y);

    this.updateColorButton();
    this.colorButton.eventMode = 'static';
    this.colorButton.cursor = 'pointer';

    this.colorButton.on('pointerdown', (e: any) => {
      e.stopPropagation();
      this.toggleColorPicker(buttonSize, buttonSize);
    });

    contentContainer.addChild(this.colorButton);
  }

  private createEyedropperButton(
    contentContainer: Container,
    x: number,
    y: number,
    buttonSize: number
  ) {
    this.eyedropperButton = new Container();
    this.eyedropperButton.position.set(x, y);

    this.updateEyedropperButton();
    this.eyedropperButton.eventMode = 'static';
    this.eyedropperButton.cursor = 'pointer';

    this.eyedropperButton.on('pointerdown', (e: any) => {
      e.stopPropagation();
      this.eyedropperMode.active = !this.eyedropperMode.active;
      this.updateEyedropperButton();
      console.log('Eyedropper mode:', this.eyedropperMode.active ? 'ON' : 'OFF');
    });

    contentContainer.addChild(this.eyedropperButton);
  }

  private createActionButtons(
    contentContainer: Container,
    startX: number,
    currentY: number,
    buttonSize: number,
    buttonGap: number,
    gridSize: number,
    cellSize: number
  ) {
    // Clear All button
    const { button: clearButton, bg: clearBg } = this.createWhiteButtonWithFlash('clean', startX, currentY, buttonSize, buttonSize);
    clearButton.on('pointerdown', (e: any) => {
      e.stopPropagation();
      this.flashButton(clearBg, buttonSize, buttonSize);
      this.gridCellsRef.forEach((cell, index) => {
        const row = Math.floor(index / gridSize);
        const col = index % gridSize;
        const x = col * cellSize;
        const y = row * cellSize;
        this.gridColorsRef[index] = 'ffffff';
        cell.clear();
        cell.rect(x, y, cellSize, cellSize);
        cell.fill({ color: 0xffffff });
        cell.stroke({ color: 0x000000, width: 1 });
      });
    });
    contentContainer.addChild(clearButton);

    // Cancel button
    const { button: cancelButton, bg: cancelBg } = this.createWhiteButtonWithFlash('cancel', startX + buttonSize + buttonGap, currentY, buttonSize, buttonSize);
    cancelButton.on('pointerdown', (e: any) => {
      e.stopPropagation();
      this.flashButton(cancelBg, buttonSize, buttonSize);
      this.hide();
      this.onClose();
    });
    contentContainer.addChild(cancelButton);

    // Submit button
    const { button: submitButton, bg: submitBg } = this.createWhiteButtonWithFlash('submit', startX + (buttonSize + buttonGap) * 2, currentY, buttonSize, buttonSize);
    submitButton.on('pointerdown', (e: any) => {
      e.stopPropagation();
      this.flashButton(submitBg, buttonSize, buttonSize);
      const colors = this.gridColorsRef;
      console.log('Grid colors (100 cells):', colors);
      console.log('Total cells:', colors.length);
      console.log('List index in 128:', listIndexIn128(colors));

      if (this.cellData.i) {
        const updatedData: UpdateCellRequest = {
          i: this.cellData.i,
          c: listIndexIn128(colors),
          t: this.cellData.t,
          l: this.cellData.l,
        };
        console.log('Updated data:', updatedData);
        this.onSave(updatedData);
      }

      this.hide();
    });
    contentContainer.addChild(submitButton);
  }

  private createWhiteButtonWithFlash(
    iconPath: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): { button: Container; bg: Graphics } {
    const button = new Container();
    const bg = new Graphics();
    bg.rect(0, 0, width, height);
    bg.fill({ color: 0xffffff }); // Nền trắng
    bg.stroke({ color: 0x000000, width: 2 });
    button.addChild(bg);

    // Load icon using asset alias
    try {
      const icon = Sprite.from(Texture.from(iconPath));
      const iconSize = 24;
      icon.width = iconSize;
      icon.height = iconSize;
      icon.position.set(
        (width - iconSize) / 2,
        (height - iconSize) / 2
      );
      button.addChild(icon);
    } catch (error) {
      console.error(`Failed to load icon with alias "${iconPath}":`, error);
    }

    button.position.set(x, y);
    button.eventMode = 'static';
    button.cursor = 'pointer';

    return { button, bg };
  }

  private flashButton(bg: Graphics, width: number, height: number) {
    bg.clear();
    bg.rect(0, 0, width, height);
    bg.fill({ color: 0xcccccc }); // Nền xám khi click
    bg.stroke({ color: 0x000000, width: 2 });
    
    // Khôi phục nền trắng sau 100ms
    setTimeout(() => {
      bg.clear();
      bg.rect(0, 0, width, height);
      bg.fill({ color: 0xffffff }); // Nền trắng
      bg.stroke({ color: 0x000000, width: 2 });
    }, 100);
  }

  private createIconButton(
    iconPath: string,
    bgColor: number,
    x: number,
    y: number,
    width: number,
    height: number
  ): Container {
    const button = new Container();
    const bg = new Graphics();
    bg.rect(0, 0, width, height);
    bg.fill({ color: bgColor });
    bg.stroke({ color: 0x000000, width: 2 });
    button.addChild(bg);

    // Load icon using asset alias
    try {
      const icon = Sprite.from(Texture.from(iconPath));
      const iconSize = 24;
      icon.width = iconSize;
      icon.height = iconSize;
      icon.position.set(
        (width - iconSize) / 2,
        (height - iconSize) / 2
      );
      button.addChild(icon);
    } catch (error) {
      console.error(`Failed to load icon with alias "${iconPath}":`, error);
    }

    button.position.set(x, y);
    button.eventMode = 'static';
    button.cursor = 'pointer';

    return button;
  }

  public hide() {
    if (!this.app?.stage || !this.popupContainer) return;

    this.app.stage.removeChild(this.popupContainer);
    this.popupContainer = null;
    this.gridCellsRef = [];
    this.gridColorsRef = [];
    this.neighborCellsRef = [];
  }

  public getColors(): string[] {
    return this.gridColorsRef;
  }
}

